/**
 * Batch Scheduler Store (Zustand)
 * UI state for batch execution
 */

import { create } from 'zustand';
import type { ExecutionPlan, SchedulerProgress, SchedulerConfig, PlanStatus } from './types';
import { DEFAULT_SCHEDULER_CONFIG } from './types';

interface BatchSchedulerState {
    // State
    currentPlan: ExecutionPlan | null;
    progress: SchedulerProgress | null;
    config: SchedulerConfig;
    isPlanning: boolean;
    isPaused: boolean;
    showPanel: boolean;

    // Actions
    setPlan: (plan: ExecutionPlan | null) => void;
    updateProgress: (progress: SchedulerProgress) => void;
    updatePlanStatus: (status: PlanStatus) => void;
    updateJobStatus: (jobId: string, updates: Partial<{ status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'; progress: number; error: string; endTime: number; startTime: number }>) => void;
    setConfig: (config: Partial<SchedulerConfig>) => void;
    setPlanning: (isPlanning: boolean) => void;
    pause: () => void;
    resume: () => void;
    cancel: () => void;
    togglePanel: () => void;
    reset: () => void;
}

export const useBatchSchedulerStore = create<BatchSchedulerState>((set, get) => ({
    // Initial state
    currentPlan: null,
    progress: null,
    config: DEFAULT_SCHEDULER_CONFIG,
    isPlanning: false,
    isPaused: false,
    showPanel: false,

    // Actions
    setPlan: (plan) => set({ currentPlan: plan, isPaused: false }),

    updateProgress: (progress) => set({ progress }),

    updatePlanStatus: (status) => set((state) => ({
        currentPlan: state.currentPlan
            ? { ...state.currentPlan, status }
            : null
    })),

    updateJobStatus: (jobId, updates) => set((state) => {
        if (!state.currentPlan) return state;

        const newJobs = [...state.currentPlan.jobs];
        const jobIndex = newJobs.findIndex(j => j.id === jobId);

        if (jobIndex === -1) return state;

        newJobs[jobIndex] = { ...newJobs[jobIndex], ...updates };

        return {
            currentPlan: { ...state.currentPlan, jobs: newJobs }
        };
    }),

    setConfig: (configUpdate) => set((state) => ({
        config: { ...state.config, ...configUpdate }
    })),

    setPlanning: (isPlanning) => set({ isPlanning }),

    pause: () => set({ isPaused: true }),

    resume: () => set({ isPaused: false }),

    cancel: () => set((state) => ({
        isPaused: false,
        currentPlan: state.currentPlan
            ? { ...state.currentPlan, status: 'cancelled' as PlanStatus }
            : null
    })),

    togglePanel: () => set((state) => ({ showPanel: !state.showPanel })),

    reset: () => set({
        currentPlan: null,
        progress: null,
        isPlanning: false,
        isPaused: false
    })
}));
